import { frontend } from '@/routes/client-errors';
import { localHref } from '@/lib/localHref';

const reportedErrors = new Set<string>();

function normalizeError(value: unknown): { name: string; message: string; stack?: string } {
    if (value instanceof Error) {
        return {
            name: value.name.slice(0, 120),
            message: value.message.slice(0, 500),
            stack: value.stack?.slice(0, 4000),
        };
    }

    return { name: 'UnknownError', message: String(value).slice(0, 500) };
}

export function reportFrontendClientError(value: unknown): void {
    try {
        if (typeof window === 'undefined') return;

        const error = normalizeError(value);
        const fingerprint = `${error.name}:${error.message}:${error.stack ?? ''}`;

        if (reportedErrors.has(fingerprint)) return;
        if (reportedErrors.size >= 20) reportedErrors.delete(reportedErrors.values().next().value ?? '');
        reportedErrors.add(fingerprint);

        // The generated actions file exports one entry per host, so the
        // callable route lives under the named routes directory. Guard the
        // call because this reporter runs inside global error handlers and
        // must never throw itself.
        if (typeof frontend !== 'function') return;

        const action = frontend();
        const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content;

        void fetch(localHref(action), {
            method: action.method,
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                ...(csrfToken ? { 'X-CSRF-TOKEN': csrfToken } : {}),
            },
            credentials: 'same-origin',
            keepalive: true,
            body: JSON.stringify({
                source: 'frontend',
                ...error,
                route: window.location.pathname,
                platform: 'web',
                release: import.meta.env.VITE_APP_VERSION,
            }),
        }).catch(() => undefined);
    } catch {
        // Error reporting must never throw.
    }
}

export function initializeFrontendClientErrorReporting(): void {
    if (typeof window === 'undefined') return;

    window.addEventListener('error', (event) => reportFrontendClientError(event.error ?? event.message));
    window.addEventListener('unhandledrejection', (event) => reportFrontendClientError(event.reason));
}
